import { FormEvent, useMemo, useState } from "react";
import {
  UseFormReturn,
  useForm,
  FieldValues,
  FormState,
  Mode,
  DefaultValues,
  UseFormProps,
  Resolver,
} from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ErrorResponse, formatErrorResponse } from "../utils";
import { useDeepEffect } from "../hooks";
import { useRegisterDirtyForm } from "./useDirtyFormRegistry";

export type FormHandler<T extends FieldValues> = {
  form: UseFormReturn<T>;
  error?: ErrorResponse;
  onSubmit: (e?: FormEvent) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolver?: Resolver<T, any, T> | undefined;
  defaultValues: DefaultValues<T> | undefined;
};

export type FormOptions<T extends FieldValues, R = void> = Partial<{
  onSuccess: (values: DefaultValues<T> | undefined, result: R) => void;
  onError?: (err: Error) => void;
  schema: z.ZodObject<z.ZodRawShape> | z.ZodEffects<z.ZodObject<z.ZodRawShape>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolver?: Resolver<T, any, T> | undefined;
  mode: Mode;
}>;

export function useFormHandler<
  T extends FieldValues = Record<string, unknown>,
  R = void,
>(
  handleSubmit: (
    values: DefaultValues<T> | undefined,
    state: FormState<T>,
  ) => R | Promise<R>,
  defaultValues: DefaultValues<T> | undefined,
  opt: FormOptions<T, R>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- This is the react-hook-form type
  formOptions?: UseFormProps<T, any>,
): FormHandler<T> {
  const form = useForm<T>({
    defaultValues,
    resolver: opt.resolver
      ? opt.resolver
      : opt.schema
        ? zodResolver(opt.schema)
        : undefined,
    mode: opt.mode,
    ...formOptions,
  });

  // react-hook-form's isSubmitSuccessful is true even when the save fails,
  // because the handler below catches errors itself; track it here instead.
  // A saved form stays clean until new defaultValues reset it.
  const [hasSaved, setHasSaved] = useState(false);
  useRegisterDirtyForm(form.formState.isDirty && !hasSaved);

  const [error, setError] = useState<ErrorResponse | undefined>();

  const onSubmit = form.handleSubmit(async values => {
    try {
      const result = await handleSubmit(values as DefaultValues<T>, form.formState);

      form.clearErrors();
      setError(undefined);
      setHasSaved(true);

      // Trigger the success handle after the errors have been cleared. This allows for
      // screen navigation to occur after the submit handler has succeeded but after
      // any errors have cleared. In doing so, we can avoid a console error from occurring
      // an complaining about a potential memory leak.
      if (opt.onSuccess) {
        opt.onSuccess(values as DefaultValues<T>, result);
      }
    } catch (err) {
      console.error("Error updating", err);
      console.error(values);
      setError(formatErrorResponse(err));
      setHasSaved(false);
      if (opt.onError && err instanceof Error) {
        opt.onError(err);
      }
    }
  });

  useDeepEffect(() => {
    // Reset the form anytime the default values change
    form.reset(defaultValues);
    setHasSaved(false);
  }, [defaultValues]);

  const returnValues = useMemo(
    () => ({
      onSubmit,
      form,
      error,
      defaultValues,
    }),
    [defaultValues, error, form, onSubmit],
  );

  return returnValues;
}
